import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { businessInfo } from '../mockData';
import { siteConfig, rates, fixedQuoteLine } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';

// ⛔ Gated page (plan §6): built pre-hire, publishes at Phase B via siteConfig.locksmithLive.
const faqs = [
  {
    id: 'e1',
    q: 'How much does a lockout cost?',
    a: `Our call-out is $${rates.callout} including GST — that covers travel and the first 15 minutes on site — then $${rates.block15} per 15 minutes after that. Most lockouts land between $120 and $180 all up, and we confirm a fixed quote before any extra work starts.`,
  },
  {
    id: 'e2',
    q: 'Will you damage my lock getting in?',
    a: 'Non-destructive entry is always the goal and usually the outcome. If a lock does have to be drilled as a last resort, we tell you first, and we carry replacement hardware on the van so you are never left unsecured.',
  },
  {
    id: 'e3',
    q: 'Do you check ID?',
    a: "Yes. We verify that you live at (or are authorised to access) the property before we open it — it's part of being a professional outfit, and it protects you too.",
  },
  {
    id: 'e4',
    q: 'Are you available overnight?',
    a: `Not yet — we currently operate ${siteConfig.hoursLine}. Overnight call-outs are coming as the team grows.`,
  },
];

const EmergencyLocksmith = () => {
  const gatedNow = !siteConfig.locksmithLive;
  usePageMeta({
    title: 'Emergency Locksmith North Shore | Shore Doors Locksmiths',
    description:
      'Locked out? Mobile emergency locksmiths for homes and businesses on Auckland\'s North Shore. Call-out $120 incl. GST with fixed quotes before extra work.',
    noindex: gatedNow,
  });

  return (
    <div className="min-h-screen">
      {gatedNow && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-start gap-3">
            <Clock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Launching soon.</span> Lockout and entry services
              start once our qualified locksmith joins the team. If a lock needs replacing or
              repairing today, we can already help —{' '}
              <a href={`tel:${businessInfo.phoneTel}`} className="underline font-medium">call {businessInfo.phone}</a>.
            </p>
          </div>
        </div>
      )}

      {/* Urgent hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-full md:w-1/2" aria-hidden="true">
          <img src="/images/stock/emergency-banner.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/50 to-gray-950/5"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-5">
              <Clock size={16} />
              {siteConfig.hoursLine} · {siteConfig.afterHoursLine}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5">
              Locked out? We'll get you back in.
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Mobile emergency locksmiths for homes and businesses across the North Shore. We'll
              be there as fast as we can — with the gear to solve it on the spot.
            </p>
            <a href={`tel:${businessInfo.phoneTel}`}>
              <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-xl px-10 py-7">
                <Phone className="mr-3" size={26} />
                Call {businessInfo.phone}
              </Button>
            </a>
            <p className="text-gray-300 text-sm mt-5">
              Call-out ${rates.callout} incl. GST (travel + first 15 minutes). {fixedQuoteLine}
            </p>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            What happens when you call
          </h2>
          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              ['1', 'Call us', 'Tell us where you are and what\'s happened. We\'ll confirm the call-out price on the phone.'],
              ['2', "We're on our way", 'A fully equipped van heads to you — we\'ll keep you posted on timing.'],
              ['3', 'Fixed quote, then the fix', 'We assess on arrival, confirm a fixed quote before any extra work, and get you back in.'],
            ].map(([n, t, d]) => (
              <div key={n} className="text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 font-bold text-xl">{n}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t}</h3>
                <p className="text-gray-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Situations + trust */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <Card className="border-gray-200">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Situations we handle</h2>
              <ul className="space-y-3">
                {[
                  'Locked out of your house or flat',
                  'Locked out of your business or shop',
                  'Keys lost or stolen — locks rekeyed so old keys stop working',
                  'Re-securing after a break-in, including hardware repairs',
                  'Broken keys stuck in locks',
                ].map((s) => (
                  <li key={s} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                <ShieldCheck className="text-blue-600" size={24} /> Who's turning up
              </h2>
              <p className="text-gray-700 mb-4">
                A professional locksmith from the team that installs commercial lock hardware
                across Auckland's hospitals, banks and public buildings — backed by a 12-month
                workmanship guarantee.
              </p>
              <p className="text-gray-700">
                Service area: {siteConfig.residentialArea}.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Emergency FAQs</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-100 text-left">
                  <span className="font-semibold text-gray-900">{f.q}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-700 leading-relaxed">{f.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <Link to="/residential" className="text-blue-600 font-semibold">
              See all residential locksmith services →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmergencyLocksmith;
